import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, Save, Loader2, ExternalLink, Mail, AlertTriangle, Layers, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatApiError } from '@/utils/apiError';

const BRANDS = [['probound', 'ProBound', '#2F5E96'], ['proepoxy', 'ProEpoxy', '#7A0B7A'], ['prodecor', 'ProDecor', '#B8860B']];

/** Values that get merged into emails/texts but aren't per-person — editable in one place. */
export function MessageFieldsPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ venue_address: '', default_start_times: { probound: '', proepoxy: '', prodecor: '' } });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 640);

  useEffect(() => {
    fetch(`${API_URL}/api/staff/message-centre/fields`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setData(d); setForm({ venue_address: d.venue_address || '', default_start_times: d.default_start_times }); })
      .catch(() => toast.error('Could not load message fields'));
  }, []);

  const dirty = data && (form.venue_address !== (data.venue_address || '') || BRANDS.some(([b]) => form.default_start_times[b] !== data.default_start_times[b]));

  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/message-centre/fields`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Save failed');
      setData(d); setForm({ venue_address: d.venue_address || '', default_start_times: d.default_start_times });
      toast.success('Saved — every new email and text uses these values');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <Card className="p-4 sm:p-5 border-2 border-[#D4A24B]/40" data-testid="message-fields-panel">
      <div className={`flex flex-wrap items-start justify-between gap-2 ${open ? 'mb-3' : ''}`}>
        <div>
          <h2 className="text-sm sm:text-base font-bold text-gray-900 flex items-center gap-2"><Layers className="w-4 h-4 text-[#D4A24B]" /> Details merged into messages</h2>
          <p className="text-xs text-gray-500 mt-0.5">These fill the <code className="font-mono">{'{{venue_address}}'}</code>, <code className="font-mono">{'{{venue_map_link}}'}</code> and <code className="font-mono">{'{{start_time}}'}</code> placeholders in every trainee email and text. Change them here — no need to edit each template.</p>
        </div>
        <div className="flex items-center gap-1.5">
          {open && <Button size="sm" onClick={save} disabled={!dirty || busy} className="bg-[#1f4030] hover:bg-[#16302a] text-white" data-testid="message-fields-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
          </Button>}
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={open ? 'Hide details' : 'Show details'} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:text-gray-900" data-testid="message-fields-toggle">
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      {open && <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Training venue address</label>
          <Input value={form.venue_address} onChange={(e) => setForm((f) => ({ ...f, venue_address: e.target.value }))} placeholder="Full address of the training venue, including postcode" className="mt-1 h-9 bg-white" data-testid="message-fields-venue" />
          {data.venue_is_placeholder && data.venue_address && <p className="mt-1 text-xs text-rose-700 inline-flex items-center gap-1" data-testid="message-fields-venue-warning"><AlertTriangle className="w-3 h-3" /> This looks like placeholder text — trainees will get "we'll confirm the address" until it's fixed.</p>}
          {!data.venue_address && <p className="mt-1 text-xs text-amber-700 inline-flex items-center gap-1" data-testid="message-fields-venue-warning"><AlertTriangle className="w-3 h-3" /> No venue set — texts say "we'll confirm the full address with you before the day".</p>}
          {data.venue_map_link && <a href={data.venue_map_link} target="_blank" rel="noreferrer" className="mt-1 text-xs text-sky-700 inline-flex items-center gap-1 hover:underline" data-testid="message-fields-map"><ExternalLink className="w-3 h-3" /> Directions link trainees receive</a>}
          <p className="mt-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Emails are sent from</p>
          <p className="text-sm text-gray-800" data-testid="message-fields-sender">{data.sender_email || 'Not configured'}</p>
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Default start time per brand</label>
          <div className="grid grid-cols-1 min-[420px]:grid-cols-3 gap-2 mt-1">
            {BRANDS.map(([key, label, colour]) => (
              <label key={key} className="block">
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colour }}>{label}</span>
                <Input type="time" value={form.default_start_times[key] || ''} onChange={(e) => setForm((f) => ({ ...f, default_start_times: { ...f.default_start_times, [key]: e.target.value } }))} className="h-9 bg-white" data-testid={`message-fields-start-${key}`} />
              </label>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-gray-500">Used unless a course has its own start time — set that on the course page (<button type="button" onClick={() => navigate('/staff/training-courses')} className="text-sky-700 hover:underline">Training courses</button>). Leave blank and the start-time line is left out.</p>
        </div>
      </div>}
    </Card>
  );
}
